/**
 * Menú «Insertar variable»: un dato del cliente (de `GET /campos`) o una variable libre.
 *
 * Elegir un dato del cliente deja ya cargados el alias, el campo sugerido y un ejemplo realista
 * (el que manda el backend): Meta pide un ejemplo por variable para aprobar, y un ejemplo como
 * «nombre» en vez de «Sofía» es de las razones más comunes de rechazo.
 *
 * Si `GET /campos` falló, el menú ofrece sólo la variable libre y lo dice: no se inventa una
 * lista de campos que el backend quizás no resuelve.
 */

import { useEffect, useId, useRef, useState } from "react";
import { Braces, ChevronDown } from "lucide-react";
import type { CampoDisponible, CampoVariable } from "../types";

interface Props {
  campos: CampoDisponible[];
  onElegir: (v: { alias: string; campoSugerido?: CampoVariable; ejemplo: string }) => void;
  /** aviso dentro del menú (p. ej. «No pudimos traer los datos de los clientes») */
  aviso?: string;
  disabled?: boolean;
}

export function InsertarVariable({ campos, onElegir, aviso, disabled = false }: Props) {
  const [abierto, setAbierto] = useState(false);
  const [pidiendoAlias, setPidiendoAlias] = useState(false);
  const [alias, setAlias] = useState("");
  const contRef = useRef<HTMLDivElement>(null);
  const idAlias = useId();
  const idMenu = useId();

  // Cerrar con un click afuera o con Escape: un menú que queda abierto tapa el texto que se edita.
  useEffect(() => {
    if (!abierto) return;
    const cerrarMenu = () => { setAbierto(false); setPidiendoAlias(false); setAlias(""); };
    const fuera = (e: MouseEvent) => {
      if (contRef.current && !contRef.current.contains(e.target as Node)) cerrarMenu();
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") cerrarMenu(); };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", esc);
    };
  }, [abierto]);

  function cerrar() {
    setAbierto(false);
    setPidiendoAlias(false);
    setAlias("");
  }

  const elegir = (v: { alias: string; campoSugerido?: CampoVariable; ejemplo: string }) => {
    onElegir(v);
    cerrar();
  };

  const confirmarLibre = () => {
    const a = alias.trim();
    if (!a) return;
    elegir({ alias: a.slice(0, 60), ejemplo: "" });
  };

  return (
    <div ref={contRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => (abierto ? cerrar() : setAbierto(true))}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-controls={abierto ? idMenu : undefined}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
      >
        <Braces size={13} /> Insertar variable <ChevronDown size={12} />
      </button>

      {abierto && (
        <div id={idMenu} role="menu" className="absolute left-0 z-30 mt-1 w-72 rounded-xl border border-slate-200 bg-white shadow-lg py-1">
          {aviso && <p className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-100">{aviso}</p>}
          {campos.map((c) => (
            <button
              key={c.campo}
              type="button"
              role="menuitem"
              onClick={() => elegir({ alias: c.etiqueta, campoSugerido: c.campo, ejemplo: c.ejemplo })}
              className="w-full text-left px-3 py-2 hover:bg-violet-50 flex items-baseline justify-between gap-3"
            >
              <span className="text-sm text-slate-800">{c.etiqueta}</span>
              <span className="text-[11px] text-slate-400 truncate">{c.ejemplo}</span>
            </button>
          ))}
          <div className={campos.length ? "border-t border-slate-100 mt-1 pt-1" : ""}>
            {!pidiendoAlias ? (
              <button type="button" role="menuitem" onClick={() => setPidiendoAlias(true)}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-violet-50">
                Otra (valor fijo)
              </button>
            ) : (
              <div className="px-3 py-2 space-y-1.5">
                <label htmlFor={idAlias} className="text-xs font-semibold text-slate-600">¿Cómo la llamamos?</label>
                <input
                  id={idAlias}
                  autoFocus
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmarLibre(); } }}
                  placeholder="Ej.: Día del evento"
                  maxLength={60}
                  className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setPidiendoAlias(false)} className="text-xs text-slate-500 px-2 py-1">Volver</button>
                  <button type="button" onClick={confirmarLibre} disabled={!alias.trim()}
                    className="text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg px-2.5 py-1 disabled:opacity-40">
                    Insertar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InsertarVariable;
